import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { useUpdateBorderColorsMutation } from 'store/services/user/meApi';
import { toast } from 'react-toastify';
import './BorderColorPickerModal.css';

interface BorderColorPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentColor1: string;
  currentColor2: string;
  avatarUrl?: string | null;
  username?: string;
  onSaveOverride?: () => void;
  onAfterSave?: () => void;
}

const DEFAULT_COLOR_1 = '#FFD700';
const DEFAULT_COLOR_2 = '#9B59B6';

const STATIC_PRESETS = [
  { label: 'Lotus', color1: '#FFD700', color2: '#9B59B6' },
  { label: 'Treasure', color1: '#FFD700', color2: '#403008' },
  { label: 'Ice', color1: '#40DCFF', color2: '#F0FCFD' },
  { label: 'Ember', color1: '#FF4500', color2: '#FF8C00' },
  { label: 'Voidslime', color1: '#36AB1D', color2: '#0E0BBF' },
];

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

/**
 * Extract two dominant colors from an image by sampling quadrants.
 */
function extractDominantColors(
  imgSrc: string,
  callback: (colors: [string, string] | null) => void
) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const size = 64;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) { callback(null); return; }

      ctx.drawImage(img, 0, 0, size, size);
      const half = size / 2;

      // Sample top-left quadrant
      const tl = ctx.getImageData(0, 0, half, half).data;
      const c1 = averageColor(tl);

      // Sample bottom-right quadrant
      const br = ctx.getImageData(half, half, half, half).data;
      const c2 = averageColor(br);

      callback([rgbToHex(c1), rgbToHex(c2)]);
    } catch {
      // CORS tainted canvas or other error
      callback(null);
    }
  };
  img.onerror = () => callback(null);
  img.src = imgSrc;
}

function averageColor(data: Uint8ClampedArray): [number, number, number] {
  let r = 0, g = 0, b = 0, count = 0;
  for (let i = 0; i < data.length; i += 4) {
    // Skip fully transparent pixels
    if (data[i + 3] < 128) continue;
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    count++;
  }
  if (count === 0) return [128, 128, 128];
  return [Math.round(r / count), Math.round(g / count), Math.round(b / count)];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase();
}

const BorderColorPickerModal: React.FC<BorderColorPickerModalProps> = ({
  isOpen,
  onClose,
  currentColor1,
  currentColor2,
  avatarUrl,
  username,
  onSaveOverride,
  onAfterSave,
}) => {
  const [color1, setColor1] = useState(currentColor1);
  const [color2, setColor2] = useState(currentColor2);
  const [hex1, setHex1] = useState(currentColor1);
  const [hex2, setHex2] = useState(currentColor2);
  const [hex1Error, setHex1Error] = useState(false);
  const [hex2Error, setHex2Error] = useState(false);
  const [avatarPreset, setAvatarPreset] = useState<{ color1: string; color2: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const avatarExtracted = useRef(false);

  const [updateBorderColors] = useUpdateBorderColorsMutation();

  // Sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      setColor1(currentColor1);
      setColor2(currentColor2);
      setHex1(currentColor1);
      setHex2(currentColor2);
      setHex1Error(false);
      setHex2Error(false);
    }
  }, [isOpen, currentColor1, currentColor2]);

  // Extract avatar colors once
  useEffect(() => {
    if (isOpen && avatarUrl && !avatarExtracted.current) {
      avatarExtracted.current = true;
      extractDominantColors(avatarUrl, (colors) => {
        if (colors) {
          setAvatarPreset({ color1: colors[0], color2: colors[1] });
        }
      });
    }
    if (!isOpen) {
      avatarExtracted.current = false;
      setAvatarPreset(null);
    }
  }, [isOpen, avatarUrl]);

  const updateColor1 = useCallback((c: string) => {
    setColor1(c);
    setHex1(c);
    setHex1Error(false);
  }, []);

  const updateColor2 = useCallback((c: string) => {
    setColor2(c);
    setHex2(c);
    setHex2Error(false);
  }, []);

  const handleHex1Change = (value: string) => {
    const v = value.startsWith('#') ? value : '#' + value;
    setHex1(v);
    if (HEX_REGEX.test(v)) {
      setColor1(v.toUpperCase());
      setHex1Error(false);
    }
  };

  const handleHex1Blur = () => {
    if (!HEX_REGEX.test(hex1)) {
      setHex1Error(true);
    }
  };

  const handleHex2Change = (value: string) => {
    const v = value.startsWith('#') ? value : '#' + value;
    setHex2(v);
    if (HEX_REGEX.test(v)) {
      setColor2(v.toUpperCase());
      setHex2Error(false);
    }
  };

  const handleHex2Blur = () => {
    if (!HEX_REGEX.test(hex2)) {
      setHex2Error(true);
    }
  };

  const handleEyeDropper = async (setColor: (c: string) => void) => {
    try {
      const dropper = new (window as any).EyeDropper();
      const result = await dropper.open();
      setColor(result.sRGBHex.toUpperCase());
    } catch {
      // User cancelled
    }
  };

  const handlePreset = (c1: string, c2: string) => {
    updateColor1(c1.toUpperCase());
    updateColor2(c2.toUpperCase());
  };

  const handleReset = () => {
    updateColor1(DEFAULT_COLOR_1);
    updateColor2(DEFAULT_COLOR_2);
  };

  const handleSave = async () => {
    if (onSaveOverride) {
      onClose();
      onSaveOverride();
      return;
    }
    setIsSaving(true);
    try {
      await updateBorderColors({ borderColor1: color1, borderColor2: color2 }).unwrap();
      toast.success('Border colors saved!');
      onClose();
      onAfterSave?.();
    } catch {
      toast.error('Failed to save border colors. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const isPresetActive = (c1: string, c2: string) =>
    color1.toUpperCase() === c1.toUpperCase() && color2.toUpperCase() === c2.toUpperCase();

  if (!isOpen) return null;

  const presets = [...STATIC_PRESETS];
  if (avatarPreset) {
    presets.splice(1, 0, { label: username || 'From Avatar', color1: avatarPreset.color1, color2: avatarPreset.color2 });
  }

  return createPortal(
    <div className="border-color-modal-overlay" onClick={onClose}>
      <div className="border-color-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="border-color-modal-header">
          <h3>Customize Avatar Border</h3>
          <button className="border-color-modal-close" onClick={onClose}>
            <img src={AssetUrls.icons.dropdowns.close} alt="Close" />
          </button>
        </div>

        {/* Body */}
        <div className="border-color-modal-body">
          {/* Live Preview */}
          <div className="border-color-preview">
            <AvatarWithFlag
              avatarUrl={avatarUrl}
              username={username}
              size={130}
              isElite={true}
              showAnimatedBorder={true}
              borderColor1={color1}
              borderColor2={color2}
            />
            <span className="border-color-preview-label">Live Preview</span>
          </div>

          {/* Presets */}
          <div className="border-color-presets">
            <div className="border-color-presets-label">Presets</div>
            <div className="border-color-presets-row">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  className={`border-color-preset ${isPresetActive(preset.color1, preset.color2) ? 'border-color-preset--active' : ''}`}
                  onClick={() => handlePreset(preset.color1, preset.color2)}
                >
                  <div
                    className="border-color-preset-swatch"
                    style={{
                      background: `conic-gradient(${preset.color1}, ${preset.color2}, ${preset.color1})`,
                    }}
                  />
                  <span className="border-color-preset-name">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Color Pickers */}
          <div className="border-color-pickers">
            {/* Color 1 */}
            <div className="border-color-picker-column">
              <div className="border-color-picker-label">Color 1</div>
              <HexColorPicker color={color1} onChange={updateColor1} />
              <div className="border-color-hex-row">
                <div
                  className="border-color-swatch-preview"
                  style={{ backgroundColor: color1 }}
                />
                <input
                  type="text"
                  className={`border-color-hex-input ${hex1Error ? 'border-color-hex-input--error' : ''}`}
                  value={hex1}
                  onChange={(e) => handleHex1Change(e.target.value)}
                  onBlur={handleHex1Blur}
                  maxLength={7}
                  placeholder="#FFD700"
                />
                {supportsEyeDropper && (
                  <button
                    className="border-color-eyedropper"
                    onClick={() => handleEyeDropper(updateColor1)}
                    title="Pick color from screen"
                  >
                    &#128065;
                  </button>
                )}
              </div>
              {hex1Error && <div className="border-color-hex-error">Invalid hex code</div>}
            </div>

            {/* Color 2 */}
            <div className="border-color-picker-column">
              <div className="border-color-picker-label">Color 2</div>
              <HexColorPicker color={color2} onChange={updateColor2} />
              <div className="border-color-hex-row">
                <div
                  className="border-color-swatch-preview"
                  style={{ backgroundColor: color2 }}
                />
                <input
                  type="text"
                  className={`border-color-hex-input ${hex2Error ? 'border-color-hex-input--error' : ''}`}
                  value={hex2}
                  onChange={(e) => handleHex2Change(e.target.value)}
                  onBlur={handleHex2Blur}
                  maxLength={7}
                  placeholder="#9B59B6"
                />
                {supportsEyeDropper && (
                  <button
                    className="border-color-eyedropper"
                    onClick={() => handleEyeDropper(updateColor2)}
                    title="Pick color from screen"
                  >
                    &#128065;
                  </button>
                )}
              </div>
              {hex2Error && <div className="border-color-hex-error">Invalid hex code</div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-color-modal-footer">
          <button className="border-color-reset-link" onClick={handleReset}>
            Reset to Default
          </button>
          <MagicButton label="Cancel" color="dark-grey" onPress={onClose} />
          <MagicButton label="Save" color="purple" onPress={handleSave} isLoading={isSaving} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BorderColorPickerModal;
